import { forwardRef, type InputHTMLAttributes } from 'react'
import { cn } from '@/utils/cn'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  icon?: React.ReactNode
  iconRight?: React.ReactNode
  highlighted?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, icon, iconRight, highlighted, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className='flex flex-col gap-1.5'>
        {label && (
          <label
            htmlFor={inputId}
            className='font-medium text-[var(--font-color-label)] uppercase tracking-wider'
            style={{ fontSize: 'var(--font-size-label)' }}
          >
            {label}
          </label>
        )}

        <div className='relative flex items-center'>
          {icon && (
            <span className='absolute left-3 text-[var(--text-muted)] pointer-events-none'>
              {icon}
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            className={cn(
              'w-full bg-[var(--glass-bg)] border border-[var(--glass-border)]',
              'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
              'rounded-md px-3 py-2 outline-none',
              'text-[13px] leading-normal',
              'transition-colors duration-150',
              'focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary-muted)]',
              highlighted &&
                !error &&
                'border-amber-500/50 bg-amber-500/5 focus:border-amber-500 focus:ring-amber-500/20',
              error && 'border-[#e5484d] focus:border-[#e5484d] focus:ring-[rgba(229,72,77,0.15)]',
              icon && 'pl-9',
              iconRight && 'pr-9',
              className
            )}
            {...props}
          />

          {iconRight && (
            <span className='absolute right-3 text-[var(--text-muted)]'>
              {iconRight}
            </span>
          )}
        </div>

        {error && (
          <p className='text-[#e5484d]' style={{ fontSize: 'var(--font-size-xs)' }}>
            {error}
          </p>
        )}
        {hint && !error && (
          <p className='text-[var(--text-muted)]' style={{ fontSize: 'var(--font-size-xs)' }}>
            {hint}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

// Textarea
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className='flex flex-col gap-1.5'>
        {label && (
          <label
            htmlFor={inputId}
            className='font-medium text-[var(--font-color-label)] uppercase tracking-wider'
            style={{ fontSize: 'var(--font-size-label)' }}
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          className={cn(
            'w-full bg-[var(--glass-bg)] border border-[var(--glass-border)]',
            'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
            'rounded-md px-3 py-2 outline-none resize-none',
            'text-[13px] leading-relaxed',
            'transition-colors duration-150',
            'focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary-muted)]',
            error && 'border-[#e5484d]',
            className
          )}
          {...props}
        />
        {error && (
          <p className='text-[#e5484d]' style={{ fontSize: 'var(--font-size-xs)' }}>
            {error}
          </p>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'
